import React, { useEffect, useState } from "react";
import axios from "axios";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API = `${ENV_API_BASE_URL}/api/vedic-mantra`;

const defaultMantraType = () => ({ name: "", sanskrit: "", transliteration: "" });
const defaultBenefit = () => ({ title: "", description: "" });
const defaultWhyBenefit = () => ({ title: "", description: "" });

const EMPTY_FORM = {
  heroTitleLine1: "VEDIC MANTRA",
  heroTitleLine2: "CHANTING",
  heroImage: "",
  heroImagePreview: "",
  mantraTitle: "What is Vedic Mantra Chanting?",
  mantraDescription: "",
  mantraDescription2: "",
  mantraHighlight: "Best yoga teacher training in India",
  mantraImage: "",
  mantraImagePreview: "",
  whyTitle: "Why Vedic Mantra Chanting?",
  whyDescription: "",
  whyBenefitItems: [],
  omSanskritText: "",
  whyImage: "",
  whyImagePreview: "",
  typesTitle: "Types of Vedic Mantras",
  typesParagraph1: "",
  typesParagraph2: "",
  typesParagraph3: "",
  typesParagraph4: "",
  mantraTypes: [],
  gayatriVideoUrl: "",
  gayatriTitle: "⊕ Gayatri Mantra:",
  gayatriLines: [],
  gayatriDesc: "",
  gayatriSanskrit: "",
  gayatriTranslation: "",
  mantraCategories: [],
  bookButtonText: "Book today",
  benefitsTitle: "Vedic Mantra & Benefits",
  benefitsIntro: "",
  benefits: [],
  benefitsImage: "",
  benefitsImagePreview: "",
  contactButtonText: "Contact Us",
  courseTitle: "Vedic Mantra Chanting Teacher Training Course",
  courseIntro: "",
  certificationTitle: "You will get Study Material+ Mantra Kit and Training Certificate after course ends!",
  donationPrice: "175",
  courseDuration: "10-15 Days (Two Times in Every Month)",
  courseEligibility: "Understanding English language",
  courseWhatYouGet: "",
  teacherImage: "",
  teacherImagePreview: "",
  rulesTitle: "RULES & REFUND POLICY",
  prerequisitesSubtitle: "◉ PREREQUISITES",
  prerequisitesContent: "",
  rulesSubtitle: "◉ RULES FOR STUDENTS",
  rulesContent: "",
  refundSubtitle: "◉ REFUND POLICY",
  refundContent: "",
};

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const Section = ({ title, children }) => {
  return (
    <div className="card mb-4">
      <div className="card-header bg-light fw-bold">
        {title}
      </div>
      <div className="card-body">
        {children}
      </div>
    </div>
  );
};

function VedicMantraForm() {
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API);
      setData(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      setData([]);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
        [`${field}Preview`]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleArrayChange = (field, index, key, value) => {
    const arr = [...(formData[field] || [])];
    if (!arr[index]) arr[index] = {};
    arr[index] = { ...arr[index], [key]: value };
    setFormData({ ...formData, [field]: arr });
  };

  const handleStringArrayChange = (field, index, value) => {
    const arr = [...(formData[field] || [])];
    arr[index] = value;
    setFormData({ ...formData, [field]: arr });
  };

  const addArrayItem = (field, defaultItem) => {
    const arr = Array.isArray(formData[field]) ? [...formData[field]] : [];
    setFormData({ ...formData, [field]: [...arr, typeof defaultItem === "function" ? defaultItem() : defaultItem] });
  };

  const removeArrayItem = (field, index) => {
    const arr = [...(formData[field] || [])];
    arr.splice(index, 1);
    setFormData({ ...formData, [field]: arr });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const payload = new FormData();

      const textFields = [
        "heroTitleLine1",
        "heroTitleLine2",
        "mantraTitle",
        "mantraDescription",
        "mantraDescription2",
        "mantraHighlight",
        "whyTitle",
        "whyDescription",
        "omSanskritText",
        "typesTitle",
        "typesParagraph1",
        "typesParagraph2",
        "typesParagraph3",
        "typesParagraph4",
        "gayatriVideoUrl",
        "gayatriTitle",
        "gayatriDesc",
        "gayatriSanskrit",
        "gayatriTranslation",
        "bookButtonText",
        "benefitsTitle",
        "benefitsIntro",
        "contactButtonText",
        "courseTitle",
        "courseIntro",
        "certificationTitle",
        "donationPrice",
        "courseDuration",
        "courseEligibility",
        "courseWhatYouGet",
        "rulesTitle",
        "prerequisitesSubtitle",
        "prerequisitesContent",
        "rulesSubtitle",
        "rulesContent",
        "refundSubtitle",
        "refundContent",
      ];

      textFields.forEach((field) => payload.append(field, formData[field] || ""));

      // Arrays: stringify for multipart
      payload.append("whyBenefitItems", JSON.stringify(formData.whyBenefitItems || []));
      payload.append("mantraTypes", JSON.stringify(formData.mantraTypes || []));
      payload.append("gayatriLines", JSON.stringify(formData.gayatriLines || []));
      payload.append("mantraCategories", JSON.stringify(formData.mantraCategories || []));
      payload.append("benefits", JSON.stringify(formData.benefits || []));

      // Images: file upload or keep existing string
      const imageFields = ["heroImage", "mantraImage", "whyImage", "benefitsImage", "teacherImage"];
      imageFields.forEach((field) => {
        const value = formData[field];
        if (value instanceof File) payload.append(field, value);
        else if (typeof value === "string") payload.append(field, value);
      });

      if (editId) {
        await axios.put(`${API}/${editId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(API, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setFormData(EMPTY_FORM);
      setEditId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error saving");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item) => {
    const normalized = { ...EMPTY_FORM, ...item };
    if (!Array.isArray(normalized.mantraTypes)) normalized.mantraTypes = [];
    if (!Array.isArray(normalized.benefits)) normalized.benefits = [];
    if (!Array.isArray(normalized.whyBenefitItems)) normalized.whyBenefitItems = [];
    if (!Array.isArray(normalized.gayatriLines)) normalized.gayatriLines = [];
    if (!Array.isArray(normalized.mantraCategories)) normalized.mantraCategories = [];

    normalized.heroImagePreview = resolveAssetUrl(item.heroImage || "");
    normalized.mantraImagePreview = resolveAssetUrl(item.mantraImage || "");
    normalized.whyImagePreview = resolveAssetUrl(item.whyImage || "");
    normalized.benefitsImagePreview = resolveAssetUrl(item.benefitsImage || "");
    normalized.teacherImagePreview = resolveAssetUrl(item.teacherImage || "");

    setFormData(normalized);
    setEditId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      await axios.delete(`${API}/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="container mt-5">
      <h2>Vedic Mantra Page Content</h2>

      {!showForm && (
        <>
          <button className="btn btn-primary mb-3" onClick={() => { setFormData(EMPTY_FORM); setEditId(null); setShowForm(true); }}>
            Create
          </button>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Mantra Title</th>
                <th>Why Title</th>
                <th>Types Title</th>
                <th>Course Title</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item._id}>
                  <td>{index + 1}</td>
                  <td>{item.mantraTitle || "-"}</td>
                  <td>{item.whyTitle || "-"}</td>
                  <td>{item.typesTitle || "-"}</td>
                  <td>{item.courseTitle || "-"}</td>
                  <td>
                    <button className="btn btn-warning btn-sm me-2" onClick={() => handleEdit(item)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item._id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-5">
          {/* Hero */}
          <Section title="Hero Section">
            <div className="row">
              <div className="col-md-4 mb-3">
                <label>Hero Title Line 1</label>
                <input type="text" name="heroTitleLine1" className="form-control" value={formData.heroTitleLine1} onChange={handleChange} />
              </div>
              <div className="col-md-4 mb-3">
                <label>Hero Title Line 2</label>
                <input type="text" name="heroTitleLine2" className="form-control" value={formData.heroTitleLine2} onChange={handleChange} />
              </div>
              <div className="col-md-4 mb-3">
                <label>Hero Image</label>
                <input type="file" className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, "heroImage")} />
                {formData.heroImagePreview && (
                  <img src={formData.heroImagePreview} alt="Hero preview" className="mt-2 rounded border" style={{ maxHeight: 100, maxWidth: "100%" }} />
                )}
              </div>
            </div>
          </Section>

          {/* What is Vedic Mantra */}
          <Section title="What is Vedic Mantra Chanting?">
            <div className="mb-3">
              <label>Section Title</label>
              <input type="text" name="mantraTitle" className="form-control" value={formData.mantraTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Paragraph 1</label>
              <textarea name="mantraDescription" className="form-control" rows={3} value={formData.mantraDescription} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Paragraph 2</label>
              <textarea name="mantraDescription2" className="form-control" rows={4} value={formData.mantraDescription2} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Highlight Text (e.g. Best yoga teacher training in India)</label>
              <input type="text" name="mantraHighlight" className="form-control" value={formData.mantraHighlight} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Section Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, "mantraImage")} />
              {formData.mantraImagePreview && (
                <img src={formData.mantraImagePreview} alt="Section preview" className="mt-2 rounded border" style={{ maxHeight: 100, maxWidth: "100%" }} />
              )}
            </div>
          </Section>

          {/* Why Vedic Mantra */}
          <Section title="Why Vedic Mantra Chanting?">
            <div className="mb-3">
              <label>Section Title</label>
              <input type="text" name="whyTitle" className="form-control" value={formData.whyTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Intro Paragraph</label>
              <textarea name="whyDescription" className="form-control" rows={2} value={formData.whyDescription} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Om Card Sanskrit Text (multiline)</label>
              <textarea name="omSanskritText" className="form-control" rows={6} value={formData.omSanskritText} onChange={handleChange} placeholder="One line per line" />
            </div>
            <div className="mb-3">
              <label>Why Section Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, "whyImage")} />
              {formData.whyImagePreview && (
                <img src={formData.whyImagePreview} alt="Why preview" className="mt-2 rounded border" style={{ maxHeight: 100, maxWidth: "100%" }} />
              )}
            </div>
            <label>Benefit Items (e.g. Mindful Start, Energetic Alignment)</label>
            {(formData.whyBenefitItems || []).map((item, i) => (
              <div key={i} className="border p-2 mb-2 rounded">
                <div className="row">
                  <div className="col">
                    <input type="text" className="form-control form-control-sm mb-1" placeholder="Title" value={item.title || ""} onChange={(e) => handleArrayChange("whyBenefitItems", i, "title", e.target.value)} />
                    <input type="text" className="form-control form-control-sm" placeholder="Description" value={item.description || ""} onChange={(e) => handleArrayChange("whyBenefitItems", i, "description", e.target.value)} />
                  </div>
                  <div className="col-auto">
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayItem("whyBenefitItems", i)}>Remove</button>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => addArrayItem("whyBenefitItems", defaultWhyBenefit)}>+ Add benefit item</button>
          </Section>

          {/* Types of Vedic Mantras */}
          <Section title="Types of Vedic Mantras">
            <div className="mb-3">
              <label>Section Title</label>
              <input type="text" name="typesTitle" className="form-control" value={formData.typesTitle} onChange={handleChange} />
            </div>
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="mb-3">
                <label>Types Paragraph {n}</label>
                <textarea name={`typesParagraph${n}`} className="form-control" rows={2} value={formData[`typesParagraph${n}`] || ""} onChange={handleChange} />
              </div>
            ))}
            <label>Mantra Types (name, Sanskrit, transliteration)</label>
            {(formData.mantraTypes || []).map((item, i) => (
              <div key={i} className="border p-2 mb-2 rounded">
                <div className="row g-2">
                  <div className="col-md-4"><input type="text" className="form-control form-control-sm" placeholder="Name" value={item.name || ""} onChange={(e) => handleArrayChange("mantraTypes", i, "name", e.target.value)} /></div>
                  <div className="col-md-4"><input type="text" className="form-control form-control-sm" placeholder="Sanskrit" value={item.sanskrit || ""} onChange={(e) => handleArrayChange("mantraTypes", i, "sanskrit", e.target.value)} /></div>
                  <div className="col-md-3"><input type="text" className="form-control form-control-sm" placeholder="Transliteration" value={item.transliteration || ""} onChange={(e) => handleArrayChange("mantraTypes", i, "transliteration", e.target.value)} /></div>
                  <div className="col-md-1"><button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayItem("mantraTypes", i)}>×</button></div>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => addArrayItem("mantraTypes", defaultMantraType)}>+ Add mantra type</button>
          </Section>

          {/* Gayatri Mantra */}
          <Section title="Gayatri Mantra Section">
            <div className="mb-3">
              <label>Video URL</label>
              <input type="text" name="gayatriVideoUrl" className="form-control" value={formData.gayatriVideoUrl} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Gayatri Title</label>
              <input type="text" name="gayatriTitle" className="form-control" value={formData.gayatriTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Gayatri Lines (one per line)</label>
              {(formData.gayatriLines || []).map((line, i) => (
                <div key={i} className="input-group mb-1">
                  <input type="text" className="form-control form-control-sm" value={line} onChange={(e) => handleStringArrayChange("gayatriLines", i, e.target.value)} />
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayItem("gayatriLines", i)}>×</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => addArrayItem("gayatriLines", "")}>+ Add line</button>
            </div>
            <div className="mb-3">
              <label>Gayatri Description</label>
              <textarea name="gayatriDesc" className="form-control" rows={2} value={formData.gayatriDesc} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Gayatri Sanskrit Text</label>
              <input type="text" name="gayatriSanskrit" className="form-control" value={formData.gayatriSanskrit} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Gayatri Translation</label>
              <input type="text" name="gayatriTranslation" className="form-control" value={formData.gayatriTranslation} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Mantra Categories (e.g. ⊕ Rigvedic Mantras)</label>
              {(formData.mantraCategories || []).map((cat, i) => (
                <div key={i} className="input-group mb-1">
                  <input type="text" className="form-control form-control-sm" value={cat} onChange={(e) => handleStringArrayChange("mantraCategories", i, e.target.value)} />
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeArrayItem("mantraCategories", i)}>×</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => addArrayItem("mantraCategories", "")}>+ Add category</button>
            </div>
            <div className="mb-3">
              <label>Book Button Text</label>
              <input type="text" name="bookButtonText" className="form-control" value={formData.bookButtonText} onChange={handleChange} />
            </div>
          </Section>

          {/* Benefits Section */}
          <Section title="Vedic Mantra & Benefits">
            <div className="mb-3">
              <label>Section Title</label>
              <input type="text" name="benefitsTitle" className="form-control" value={formData.benefitsTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Intro Paragraph</label>
              <textarea name="benefitsIntro" className="form-control" rows={2} value={formData.benefitsIntro} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Benefits Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, "benefitsImage")} />
              {formData.benefitsImagePreview && (
                <img src={formData.benefitsImagePreview} alt="Benefits preview" className="mt-2 rounded border" style={{ maxHeight: 100, maxWidth: "100%" }} />
              )}
            </div>
            <label>Benefit Cards (title + description)</label>
            {(formData.benefits || []).map((item, i) => (
              <div key={i} className="border p-2 mb-2 rounded">
                <div className="mb-1">
                  <input type="text" className="form-control form-control-sm" placeholder="Title" value={item.title || ""} onChange={(e) => handleArrayChange("benefits", i, "title", e.target.value)} />
                </div>
                <div className="d-flex">
                  <textarea className="form-control form-control-sm flex-grow-1 me-2" rows={2} placeholder="Description" value={item.description || ""} onChange={(e) => handleArrayChange("benefits", i, "description", e.target.value)} />
                  <button type="button" className="btn btn-outline-danger btn-sm align-self-start" onClick={() => removeArrayItem("benefits", i)}>Remove</button>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => addArrayItem("benefits", defaultBenefit)}>+ Add benefit</button>
            <div className="mt-3">
              <label>Contact Button Text</label>
              <input type="text" name="contactButtonText" className="form-control" value={formData.contactButtonText} onChange={handleChange} />
            </div>
          </Section>

          {/* Course Section */}
          <Section title="Course Section">
            <div className="mb-3">
              <label>Course Title</label>
              <input type="text" name="courseTitle" className="form-control" value={formData.courseTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Course Intro</label>
              <textarea name="courseIntro" className="form-control" rows={3} value={formData.courseIntro} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Certification Title</label>
              <input type="text" name="certificationTitle" className="form-control" value={formData.certificationTitle} onChange={handleChange} />
            </div>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label>Donation Price (number only, e.g. 175)</label>
                <input type="text" name="donationPrice" className="form-control" value={formData.donationPrice} onChange={handleChange} />
              </div>
              <div className="col-md-4 mb-3">
                <label>Duration</label>
                <input type="text" name="courseDuration" className="form-control" value={formData.courseDuration} onChange={handleChange} />
              </div>
              <div className="col-md-4 mb-3">
                <label>Eligibility</label>
                <input type="text" name="courseEligibility" className="form-control" value={formData.courseEligibility} onChange={handleChange} />
              </div>
            </div>
            <div className="mb-3">
              <label>What You Get</label>
              <textarea name="courseWhatYouGet" className="form-control" rows={3} value={formData.courseWhatYouGet} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Teacher Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(e) => handleFileChange(e, "teacherImage")} />
              {formData.teacherImagePreview && (
                <img src={formData.teacherImagePreview} alt="Teacher preview" className="mt-2 rounded border" style={{ maxHeight: 100, maxWidth: "100%" }} />
              )}
            </div>
          </Section>

          {/* Rules & Refund */}
          <Section title="Rules & Refund Policy">
            <div className="mb-3">
              <label>Section Title</label>
              <input type="text" name="rulesTitle" className="form-control" value={formData.rulesTitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Prerequisites Subtitle</label>
              <input type="text" name="prerequisitesSubtitle" className="form-control" value={formData.prerequisitesSubtitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Prerequisites Content</label>
              <textarea name="prerequisitesContent" className="form-control" rows={4} value={formData.prerequisitesContent} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Rules for Students Subtitle</label>
              <input type="text" name="rulesSubtitle" className="form-control" value={formData.rulesSubtitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Rules Content</label>
              <textarea name="rulesContent" className="form-control" rows={4} value={formData.rulesContent} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Refund Subtitle</label>
              <input type="text" name="refundSubtitle" className="form-control" value={formData.refundSubtitle} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label>Refund Content</label>
              <textarea name="refundContent" className="form-control" rows={4} value={formData.refundContent} onChange={handleChange} />
            </div>
          </Section>

          <div className="mb-3">
            <button type="submit" className="btn btn-success" disabled={uploading}>{uploading ? "Saving..." : (editId ? "Update" : "Save")}</button>
            <button type="button" className="btn btn-secondary ms-2" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default VedicMantraForm;
